import React, { useContext, useState, useEffect, useRef } from "react";
import { ProjectContext } from "../../context/project_context";
import TimecodeInput from "../../components/TimecodeInput";
import debounce from 'lodash/debounce';

const ProjectSentenceThoughtFixing = () => {
  const {
    project,
    updateProject,
    fetchEnglishTranscripts,
    english_transcripts,
    handleSaveSentenceTranscript,
    updateEnglishTranscriptTimecodes,
    // Media playback
    activeTranscriptId,
    autoScrollEnabled,
    seekToTranscript
  } = useContext(ProjectContext);
  
  const [loading, setLoading] = useState(false);
  const [editedThoughts, setEditedThoughts] = useState({});
  const [savingStates, setSavingStates] = useState({});

  // Create a memoized debounced function for each transcript
  const [debouncedFunctions] = useState(() => ({}));

  // Refs for auto-scrolling
  const rowRefs = useRef({});

  useEffect(() => {
    if (project?.id) {
      fetchEnglishTranscripts();
    }
  }, [project?.id]);

  // Auto-scroll to active row
  useEffect(() => {
    if (autoScrollEnabled && activeTranscriptId && rowRefs.current[activeTranscriptId]) {
      rowRefs.current[activeTranscriptId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeTranscriptId, autoScrollEnabled]);

  // Listen for focus active transcript event (Shift+Enter shortcut)
  useEffect(() => {
    const handleFocusActive = () => {
      if (activeTranscriptId && rowRefs.current[activeTranscriptId]) {
        const row = rowRefs.current[activeTranscriptId];
        const textarea = row.querySelector('.thought-textarea');
        if (textarea) {
          textarea.focus();
          textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    };

    window.addEventListener('focusActiveTranscript', handleFocusActive);
    return () => window.removeEventListener('focusActiveTranscript', handleFocusActive);
  }, [activeTranscriptId]);

  // Listen for seek to focused transcript event (Ctrl+Enter shortcut)
  useEffect(() => {
    const handleSeekToFocused = () => {
      // Find which transcript's textarea is currently focused
      const focusedTextarea = document.activeElement;
      if (focusedTextarea?.classList.contains('thought-textarea')) {
        // Find the parent row and get the transcript ID
        const row = focusedTextarea.closest('tr');
        if (row) {
          const transcriptId = Object.keys(rowRefs.current).find(
            id => rowRefs.current[id] === row
          );
          if (transcriptId) {
            const transcript = english_transcripts.find(t => t.id === parseInt(transcriptId));
            if (transcript) {
              seekToTranscript(transcript);
            }
          }
        }
      }
    };

    window.addEventListener('seekToFocusedTranscript', handleSeekToFocused);
    return () => window.removeEventListener('seekToFocusedTranscript', handleSeekToFocused);
  }, [english_transcripts, seekToTranscript]);

  const handleThoughtChange = (transcriptId, newThought) => {
    setEditedThoughts(prev => ({
      ...prev,
      [transcriptId]: newThought
    }));

    if (debouncedFunctions[transcriptId]) {
      debouncedFunctions[transcriptId].cancel();
    }

    if (!debouncedFunctions[transcriptId]) {
      debouncedFunctions[transcriptId] = debounce(async (thought) => {
        try {
          setSavingStates(prev => ({ ...prev, [transcriptId]: true }));
          const success = await handleSaveSentenceTranscript(transcriptId, thought);
          if (success) {
            setSavingStates(prev => ({ ...prev, [transcriptId]: false }));
          }
        } catch (error) {
          console.error('Failed to save transcript:', error);
          setSavingStates(prev => ({ ...prev, [transcriptId]: false }));
        }
      }, 1000);
    }

    debouncedFunctions[transcriptId](newThought);
  };

  const handleTimecodeChange = async (transcriptId, field, newValue) => {
    const transcript = english_transcripts.find(t => t.id === transcriptId);
    if (!transcript) return;

    const startTime = field === 'start_time' ? newValue : parseFloat(transcript.start_time);
    const endTime = field === 'end_time' ? newValue : parseFloat(transcript.end_time);

    if (endTime <= startTime) {
      alert('End time must be greater than start time');
      return;
    }

    try {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: true }));
      await updateEnglishTranscriptTimecodes(transcriptId, startTime, endTime);
    } catch (error) {
      console.error('Failed to update timecodes:', error);
      alert('Failed to update timecodes');
    } finally {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: false }));
    }
  };

  const handleBack = async () => {
    await updateProject({
      ...project,
      project_status: "sentence_building"
    });
  };

  const handleComplete = async () => {
    await updateProject({
      ...project,
      project_status: "translate_to_english"
    });
  };

  const handleKeyDown = (e, currentIndex, transcriptId) => {
    const textareas = document.querySelectorAll('.thought-textarea');

    switch (e.key) {
      case 'ArrowUp':
        if (currentIndex > 0) {
          textareas[currentIndex - 1].focus();
        }
        break;
      case 'ArrowDown':
        if (currentIndex < textareas.length - 1) {
          textareas[currentIndex + 1].focus();
        }
        break;
      case 'Enter':
        if (!e.shiftKey && !e.ctrlKey) {
          e.preventDefault();
          if (currentIndex < textareas.length - 1) {
            textareas[currentIndex + 1].focus();
          }
        }
        break;
    }
  };

  const handleRowClick = (transcript) => {
    seekToTranscript(transcript);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="sentence-thought-fixing">
      <h2>Fix Sentence Thoughts</h2>
      <div className="transcripts-list mt-3">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>T#</th>
              <th style={{ width: '240px' }}>Timecodes</th>
              <th>Sentence</th>
              <th style={{ width: '100px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {english_transcripts.map((transcript, index) => {
              const isActive = transcript.id === activeTranscriptId;
              
              return (
                <tr 
                  key={transcript.id}
                  ref={el => rowRefs.current[transcript.id] = el}
                  className={isActive ? 'table-info' : ''}
                  style={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <td 
                    style={{ width: '50px' }}
                    onClick={() => handleRowClick(transcript)}
                  >
                    {transcript.sequence_number}
                  </td>
                  <td style={{ width: '240px' }}>
                    <div className="d-flex flex-column gap-1">
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted" style={{ width: '35px' }}>Start:</small>
                        <TimecodeInput
                          value={parseFloat(transcript.start_time)}
                          onChange={(val) => handleTimecodeChange(transcript.id, 'start_time', val)}
                        />
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted" style={{ width: '35px' }}>End:</small>
                        <TimecodeInput
                          value={parseFloat(transcript.end_time)}
                          onChange={(val) => handleTimecodeChange(transcript.id, 'end_time', val)}
                        />
                      </div>
                      {savingStates[`timecode_${transcript.id}`] && (
                        <small className="text-muted">Saving...</small>
                      )}
                    </div>
                  </td>
                  <td className="p-1">
                    <textarea
                      className="form-control thought-textarea border-0 rounded-0"
                      defaultValue={transcript.original_text}
                      onChange={(e) => handleThoughtChange(transcript.id, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index, transcript.id)}
                      rows="2"
                    />
                  </td>
                  <td style={{ width: '100px' }}>
                    {(editedThoughts[transcript.id] && savingStates[transcript.id]) && (
                      <span className="text-muted small">Saving...</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex gap-2 mt-3">
        <button 
          className="btn btn-secondary"
          onClick={handleBack}
        >
          Back to Sentence Building
        </button>
        <button
          className="btn btn-primary"
          onClick={handleComplete}
          disabled={loading}
        >
          Complete Thought Fixing
        </button>
      </div>
    </div>
  );
};

export default ProjectSentenceThoughtFixing;
